/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { type JSX } from 'react';
import { observable, action, when, type IReactionDisposer, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

type ContextMenuProps = {
  /** Attach to a specific element via ref. Mutually exclusive with `selector`. */
  container?: React.RefObject<HTMLElement | null>;
  /**
   * CSS selector for delegated event handling.
   * When set, any click on an element matching this selector (or its descendant)
   * will open the menu anchored to the matched element.
   * The matched element is passed as `popupElement` to `children`, so you can
   * read `popupElement.dataset.*` to distinguish which item was clicked.
   * Mutually exclusive with `container`.
   */
  selector?: string;
  showOnClick?: boolean;
  onAppear?: (popupElement: HTMLElement) => boolean;
  onDisappear?: () => void;
  children(popupElement: HTMLElement): JSX.Element | null;
};

@observer
export default class ContextMenu extends React.Component<ContextMenuProps> {
  private _contextMenu: React.RefObject<HTMLDivElement | null> = React.createRef();
  private _popupMouseEvent: MouseEvent | null = null;
  private _contextMenuWhenDispower: IReactionDisposer | null = null;
  private _containerListenersAttached = false;
  @observable private _isVisible = false;
  @observable.ref private _popupElement: HTMLElement | null = null;
  @observable private _container: React.RefObject<HTMLElement | null> | null = null;

  constructor(props: ContextMenuProps){
    super(props);
    makeObservable(this);

    if (props.container) {
      this._container = props.container;
      // when() fires if the ref is already set during the render phase (e.g. pre-existing DOM nodes).
      // For newly-mounted conditionally-rendered buttons the ref is still null here, so
      // we also attach in componentDidMount (where React has committed all refs).
      this._contextMenuWhenDispower = when(() => !!(this._container && this._container.current), () => {
        this._attachContainerListeners();
      });
    }
  }

  private _attachContainerListeners() {
    if (this._containerListenersAttached) return;
    const { container } = this.props;
    if (container?.current) {
      container.current.addEventListener('contextmenu', this._onTargetContextMenu);
      container.current.addEventListener('click', this._onTargetClick);
      container.current.addEventListener('scroll', this._onDocumentScroll);
      this._containerListenersAttached = true;
    }
  }

  componentDidMount() {
    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('scroll', this._onDocumentScroll);
    document.addEventListener('contextmenu', this._onDocumentContextMenu, { capture: true });
    document.addEventListener('keydown', this._onKeydownPress);
    window.addEventListener('resize', this._onDocumentResize);

    // Selector (delegated) mode – single listener covers all matching elements
    if (this.props.selector) {
      document.addEventListener('click', this._onDelegatedClick, { capture: true });
    }

    // By the time componentDidMount runs React has committed all refs, so the container
    // ref (e.g. a sibling button) is guaranteed to be set even if it was null during the
    // render phase when the constructor's when() first evaluated.
    if (this.props.container) {
      this._attachContainerListeners();
    }
  }

  componentWillUnmount() {
    const { container } = this.props;
    if (container?.current) container.current.removeEventListener('contextmenu', this._onTargetContextMenu);
    if (container?.current) container.current.removeEventListener('click', this._onTargetClick);
    if (container?.current) container.current.removeEventListener('scroll', this._onDocumentScroll);
    this._containerListenersAttached = false;
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('scroll', this._onDocumentScroll);
    document.removeEventListener('contextmenu', this._onDocumentContextMenu, { capture: true });
    document.removeEventListener('keydown', this._onKeydownPress);
    window.removeEventListener('resize', this._onDocumentResize);
    if (this.props.selector) {
      document.removeEventListener('click', this._onDelegatedClick, { capture: true });
    }
    if (this._contextMenuWhenDispower){
      this._contextMenuWhenDispower();
      this._contextMenuWhenDispower = null;
    }
  }

  @action.bound
  private _onDocumentResize() {
    this._hide();
  }

  @action.bound
  private _onDocumentContextMenu(event: MouseEvent) {
    const prevElement = this._popupElement;
    this._popupElement = event.target as HTMLElement;

    if(prevElement !== this._popupElement){
      this._onDocumentScroll();
    }
  }

  @action
  componentDidUpdate(){
    this._container = this.props.container ?? null;
    this._updateMenuPosition();
  }

  render(){
    if (!this._isVisible) return null;

    return (
      <div ref={this._contextMenu} className="contextMenu" onContextMenu={(e) => this._onTargetContextMenu(e as unknown as MouseEvent)}>
        {this._popupElement && this.props.children(this._popupElement)}
      </div>
    );
  }

  @action.bound
  private _onKeydownPress(event: KeyboardEvent){
    if (event.code === 'Escape') {
      this._isVisible = false;
    }
  }

  /** Delegated click handler – fires for any element matching `props.selector`. */
  @action.bound
  private _onDelegatedClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const matched = target.closest(this.props.selector!) as HTMLElement | null;
    if (!matched) return;

    // Prevent the bubbling-phase _onDocumentClick from immediately closing
    event.stopPropagation();

    // Toggle: clicking the same trigger closes the menu
    if (this._isVisible && this._popupElement === matched) {
      this._hide();
      return;
    }

    this._popupElement = matched;

    if (this.props.onAppear && !this.props.onAppear(matched)) return;

    this._isVisible = true;
    this._popupMouseEvent = event;
    setTimeout(this._updateMenuPosition, 0);
  }

  @action.bound
  private _onTargetClick(event: MouseEvent){
    const menuTarget = (event.target as HTMLElement).closest('.context-menu-target');
    if (menuTarget && event.button === 0 /* Left */ && !this.props.showOnClick) {
      event.preventDefault();
      event.stopPropagation();
      this._onTargetContextMenu(event);
    }

    if (this.props.showOnClick){
      this._show(event);
    }
  }
  
  @action.bound
  private _onTargetContextMenu(event: MouseEvent) {
    if (this.props.showOnClick) {
      return;
    }

    event.preventDefault();
    this._show(event);
  }

  @action.bound
  private _show(event: MouseEvent){
    this._popupElement = event.currentTarget as HTMLElement;

    if (this.props.onAppear) {
      if (!this.props.onAppear(this._popupElement)){
        return;
      }
    }

    this._isVisible = true;
    this._popupMouseEvent = event;

    setTimeout(this._updateMenuPosition, 0);
  }

  @action.bound
  private _onDocumentClick(event: MouseEvent) {
    // Selector mode: delegated capture handler manages show/toggle;
    // here we just close on any click that isn't a submenu item.
    if (this.props.selector) {
      const target = event.target as HTMLElement;
      if (target.classList.contains('menu-action') || target.classList.contains('with-children')) return;
      // Don't close the menu when clicking the trigger element itself —
      // _onDelegatedClick already handles open/toggle for trigger clicks.
      if (target.closest(this.props.selector)) return;
      if (this._isVisible) this._hide();
      return;
    }

    if (this.props.showOnClick) {
      //check if clicked on container
      const container = this._container?.current;
      if (container && container.contains(event.target as Node)) {
        return;
      }
    }

    const target = event.target as HTMLDivElement;
    if (!target || target.classList.contains('menu-action') || target.classList.contains('with-children')) return;
    
    if (this._isVisible || target !== this._contextMenu.current){
      this._hide();
    }
  }

  @action.bound
  private _hide(){
    this.props.onDisappear?.();
    this._isVisible = false;
  }

  @action.bound
  private _onDocumentScroll() {
    if (this._isVisible) this._hide();
  }

  @action.bound
  private _updateMenuPosition() {
    if (!this._popupMouseEvent) return;

    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const contextMenu = this._contextMenu.current;
    if (!contextMenu) return;

    const rootW = contextMenu.offsetWidth;
    const rootH = contextMenu.offsetHeight;

    // Selector mode: anchor to the matched element's bounding rect
    if (this.props.selector && this._popupElement) {
      const rect = this._popupElement.getBoundingClientRect();
      // If the anchor element is hidden (display:none) or detached, its rect is all zeros.
      // Skip repositioning to avoid jumping to (0,0).
      if (rect.width === 0 && rect.height === 0) return;
      const left = Math.max(5, Math.min(rect.right - rootW, screenW - rootW - 5));
      contextMenu.style.left = `${left}px`;
      if (screenH - rect.bottom >= rootH + 5) {
        contextMenu.style.top = `${rect.bottom + 5}px`;
      } else {
        contextMenu.style.top = `${rect.top - rootH - 5}px`;
      }
      return;
    }

    if (this.props.showOnClick) {
      // Position centered below (or above) the container element
      const container = this._container?.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;

        // Clamp horizontally so menu stays within viewport
        const left = Math.max(5, Math.min(centerX - rootW / 2, screenW - rootW - 5));
        contextMenu.style.left = `${left}px`;

        // Show below if there's space, otherwise above
        if (screenH - rect.bottom >= rootH + 5) {
          contextMenu.style.top = `${rect.bottom + 5}px`;
        } else {
          contextMenu.style.top = `${rect.top - rootH - 5}px`;
        }
      }
      return;
    }

    const clickX = this._popupMouseEvent.clientX;
    const clickY = this._popupMouseEvent.clientY;

    const right = (screenW - clickX) > rootW;
    const top = (screenH - clickY) > rootH;

    contextMenu.style.left = right ? `${clickX + 5}px` : `${clickX - rootW - 5}px`;
    contextMenu.style.top  = top   ? `${clickY + 5}px` : `${clickY - rootH - 5}px`;
  }
}